import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut, User } from 'lucide-react';
import { Button } from './ui/button';
import Logo from './Logo';
import { useAuth } from '../contexts/AuthContext';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();

  // Scroll to top when location changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Get Funded', path: '/get-funded' },
    { name: 'Lab Partners', path: "/lab-partners" },
    { name: 'About Us', path: '/about' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' }
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav
      className={
        'fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm shadow-lg border-b border-[#10dfb2]/20'
      }
    >
      <div className="max-w-7xl mx-auto pr-4 sm:pr-6 lg:pr-8 pl-2 sm:pl-4">
        <div className="flex justify-between items-center h-24">
          {/* Logo - push further left using negative margin to offset container padding */}
          <Link to="/" className="flex items-center space-x-2 ml-[-12px] sm:ml-[-24px] lg:ml-[-48px] overflow-hidden">
            <Logo className="h-20 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-base font-medium transition-colors hover:text-[#10dfb2] ${
                  location.pathname === link.path ? 'text-[#10dfb2]' : 'text-gray-300'
                }`}
              >
                {link.name}
              </Link>
            ))}
            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <Link to="#"> 
                  <Button
                    variant="ghost"
                    className="text-[#10dfb2] hover:text-[#10dfb2] hover:bg-[#1A1A1A]"
                  >
                    <User size={18} className="mr-2" />
                    {user?.name}
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="text-gray-300 hover:text-[#10dfb2] hover:bg-[#1A1A1A]"
                >
                  <LogOut size={18} />
                </Button>
              </div>
            ) : (
              <>
                <a href="https://fundedlabdashboard.propaccount.com/en/sign-in" target="_blank" rel="noopener noreferrer">
                  <Button
                    variant="ghost"
                    className="text-gray-300 hover:text-[#10dfb2]"
                  >
                    Login
                  </Button>
                </a>
                <a href="https://fundedlabdashboard.propaccount.com/en/sign-up" target="_blank" rel="noopener noreferrer">
                  <Button
                    variant="default"
                    size="lg"
                    className="bg-[#10dfb2] hover:bg-[#10dfb2] text-black font-semibold"
                  >
                    Register
                  </Button>
                </a>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className={'text-white'} size={28} />
            ) : (
              <Menu className={'text-white'} size={28} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-black/95 backdrop-blur-sm border-t border-[#10dfb2]/20">
          <div className="px-4 pt-2 pb-6 space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="block py-2 text-gray-300 hover:text-[#10dfb2] font-medium"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            {isAuthenticated ? (
              <>
                <Link to="#" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button className="w-full bg-[#10dfb2] hover:bg-[#10dfb2] text-black font-semibold mb-2">
                    Dashboard
                  </Button>
                </Link>
                <Button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  variant="outline"
                  className="w-full border-[#10dfb2] text-[#10dfb2]"
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <a href="https://fundedlabdashboard.propaccount.com/en/sign-in" target="_blank" rel="noopener noreferrer" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full border-[#10dfb2] text-[#10dfb2] mb-2">
                    Login
                  </Button>
                </a>
                <a href="https://fundedlabdashboard.propaccount.com/en/sign-up" target="_blank" rel="noopener noreferrer" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button className="w-full bg-[#10dfb2] hover:bg-[#10dfb2] text-black font-semibold">
                    Register
                  </Button>
                </a>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
